// custom-multi-select.component.ts
import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  label: string;
  value: any;
}

@Component({
  selector: 'app-custom-multi-select',
  template: `
    <div class="custom-select-wrapper">
      <div class="custom-select-container" 
           [class.focused]="isFocused"
           [class.open]="isOpen"
           (click)="toggleDropdown()">
        
        <div class="selected-values" *ngIf="selectedOptions.length > 0">
          <span class="selected-tag" *ngFor="let option of selectedOptions; trackBy: trackByValue">
            {{ option.label }}
            <i class="remove-icon" (click)="removeOption($event, option)">×</i>
          </span>
        </div>
        
        <input type="text" 
               class="search-input"
               [value]="searchTerm"
               [placeholder]="getPlaceholder()"
               (focus)="onFocus()"
               (blur)="onBlur()"
               (input)="onSearch($event)"
               (keydown)="onKeydown($event)"
               #searchInput>
        
        <div class="dropdown-arrow" [class.rotated]="isOpen">
          <svg width="12" height="8" viewBox="0 0 12 8">
            <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </div>
      </div>
      
      <div class="dropdown-panel" *ngIf="isOpen && filteredOptions.length > 0">
        <div class="option-item"
             *ngFor="let option of filteredOptions; trackBy: trackByValue"
             [class.selected]="isSelected(option)"
             [class.highlighted]="highlightedIndex === getOptionIndex(option)"
             (click)="selectOption(option)">
          <span class="option-text">{{ option.label }}</span>
          <span class="checkmark" *ngIf="isSelected(option)">✓</span>
        </div>
      </div>
      
      <div class="dropdown-panel empty" *ngIf="isOpen && filteredOptions.length === 0">
        <div class="no-options">Tidak ada data ditemukan</div>
      </div>
    </div>
  `,
  styleUrls: ['./custom-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMultiSelectComponent),
      multi: true
    }
  ]
})
export class CustomMultiSelectComponent implements ControlValueAccessor, OnInit {
  @Input() options: SelectOption[] = [];
  @Input() placeholder: string = 'Pilih opsi...';
  @Input() disabled: boolean = false;
  @Output() selectionChange = new EventEmitter<any[]>();

  selectedOptions: SelectOption[] = [];
  filteredOptions: SelectOption[] = [];
  searchTerm: string = '';
  isOpen: boolean = false;
  isFocused: boolean = false;
  highlightedIndex: number = -1;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  ngOnInit() {
    this.filteredOptions = [...this.options];
  }

  // ControlValueAccessor implementation
  writeValue(value: any[]): void {
    if (value && Array.isArray(value)) {
      this.selectedOptions = this.options.filter(option => 
        value.includes(option.value)
      );
    } else {
      this.selectedOptions = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Component methods
  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.filterOptions();
    }
  }

  onFocus(): void {
    this.isFocused = true;
    this.isOpen = true;
    this.filterOptions();
  }

  onBlur(): void {
    // Delay to allow click events to process
    setTimeout(() => {
      this.isFocused = false;
      this.isOpen = false;
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
      this.onTouched();
    }, 200);
  }

  onSearch(event: any): void {
    this.searchTerm = event.target.value;
    this.filterOptions();
    this.highlightedIndex = 0;
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.filteredOptions[this.highlightedIndex]) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.isOpen = false;
        break;
    }
  }

  selectOption(option: SelectOption): void {
    const isCurrentlySelected = this.isSelected(option);
    
    if (isCurrentlySelected) {
      this.selectedOptions = this.selectedOptions.filter(
        selected => selected.value !== option.value
      );
    } else {
      this.selectedOptions.push(option);
    }

    this.emitChange();
    this.searchTerm = '';
    this.filterOptions();
  }

  removeOption(event: Event, option: SelectOption): void {
    event.stopPropagation();
    this.selectedOptions = this.selectedOptions.filter(
      selected => selected.value !== option.value
    );
    this.emitChange();
  }

  isSelected(option: SelectOption): boolean {
    return this.selectedOptions.some(selected => selected.value === option.value);
  }

  getPlaceholder(): string {
    return this.selectedOptions.length > 0 ? 'Cari lagi...' : this.placeholder;
  }

  private filterOptions(): void {
    if (!this.searchTerm.trim()) {
      this.filteredOptions = [...this.options];
    } else {
      this.filteredOptions = this.options.filter(option =>
        option.label.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }
    this.highlightedIndex = 0;
  }

  private highlightNext(): void {
    if (this.highlightedIndex < this.filteredOptions.length - 1) {
      this.highlightedIndex++;
    }
  }

  private highlightPrevious(): void {
    if (this.highlightedIndex > 0) {
      this.highlightedIndex--;
    }
  }

  private getOptionIndex(option: SelectOption): number {
    return this.filteredOptions.findIndex(opt => opt.value === option.value);
  }

  private emitChange(): void {
    const values = this.selectedOptions.map(option => option.value);
    this.onChange(values);
    this.selectionChange.emit(values);
  }

  trackByValue(index: number, option: SelectOption): any {
    return option.value;
  }
}